import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getChainName, getExplorerTxUrl } from "../../../anyspend/index.js";
import { useGasPrice } from "../../../anyspend/react/index.js";
import { normalizeAddress } from "../../../anyspend/utils/index.js";
import { Button, ShinyButton, StyleRoot, toast, TransitionPanel, useAccountWallet, useModalStore } from "../../../global-account/react/index.js";
import { cn } from "../../../shared/utils/cn.js";
import { formatUnits } from "../../../shared/utils/number.js";
import invariant from "invariant";
import { ArrowDown, Loader2 } from "lucide-react";
import { AnimatedCheckmark } from "./icons/AnimatedCheckmark.js";
import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnySpendCustomizationProvider, useAnySpendCustomization } from "./context/AnySpendCustomizationContext.js";
import { useSetActiveWallet } from "thirdweb/react";
import { B3_TOKEN } from "../../constants/index.js";
import { useDirectTransfer } from "../hooks/useDirectTransfer.js";
import { generateEncodedData, PanelView, useAnyspendFlow } from "../hooks/useAnyspendFlow.js";
import { AnySpendFingerprintWrapper, getFingerprintConfig } from "./AnySpendFingerprintWrapper.js";
import { CryptoPaySection } from "./common/CryptoPaySection.js";
import { CryptoPaymentMethod, CryptoPaymentMethodType } from "./common/CryptoPaymentMethod.js";
import { CryptoReceiveSection } from "./common/CryptoReceiveSection.js";
import { FeeDetailPanel } from "./common/FeeDetailPanel.js";
import { FiatPaymentMethod, FiatPaymentMethodComponent } from "./common/FiatPaymentMethod.js";
import { GasIndicator } from "./common/GasIndicator.js";
import { OrderDetails } from "./common/OrderDetails.js";
import { PanelOnramp } from "./common/PanelOnramp.js";
import { PointsDetailPanel } from "./common/PointsDetailPanel.js";
import { RecipientSelection } from "./common/RecipientSelection.js";
const SLIPPAGE_PERCENT = 3;
export function AnySpendCustomExactIn(props) {
  const fingerprintConfig = getFingerprintConfig();
  return _jsx(AnySpendFingerprintWrapper, {
    fingerprint: fingerprintConfig,
    children: _jsx(AnySpendCustomizationProvider, {
      slots: props.slots,
      content: props.content,
      theme: props.theme,
      children: _jsx(AnySpendCustomExactInInner, {
        ...props
      })
    })
  });
}
function AnySpendCustomExactInInner({
  loadOrder,
  mode = "modal",
  recipientAddress,
  paymentType = "crypto",
  sourceTokenAddress,
  sourceTokenChainId,
  destinationToken,
  destinationChainId,
  onSuccess,
  onOpenCustomModal,
  mainFooter,
  onTokenSelect,
  customUsdInputValues,
  preferEoa,
  customExactInConfig,
  destinationTokenAmount,
  orderType = "custom_exact_in",
  minDestinationAmount,
  header,
  returnToHomeUrl,
  customRecipientLabel,
  returnHomeLabel,
  classes,
  allowDirectTransfer = false,
  callbackMetadata,
  senderAddress,
  showFiatOption = true
}) {
  const {
    slots,
    content
  } = useAnySpendCustomization();
  const effectivePaymentType = showFiatOption ? paymentType : "crypto";
  const actionLabel = customExactInConfig?.action ?? "Custom Execution";
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const DESTINATION_TOKEN_DETAILS = {
    SYMBOL: destinationToken.symbol ?? "TOKEN",
    LOGO_URI: destinationToken.metadata?.logoURI ?? ""
  };
  const {
    activePanel,
    setActivePanel,
    orderId,
    setOrderId,
    oat,
    selectedSrcChainId,
    setSelectedSrcChainId,
    selectedSrcToken,
    setSelectedSrcToken,
    selectedDstToken,
    selectedDstChainId,
    srcAmount,
    setSrcAmount,
    dstAmount,
    dstAmountInput,
    setDstAmountInput,
    isSrcInputDirty,
    setIsSrcInputDirty,
    tradeType,
    selectedCryptoPaymentMethod,
    effectiveCryptoPaymentMethod,
    setSelectedCryptoPaymentMethod,
    selectedFiatPaymentMethod,
    setSelectedFiatPaymentMethod,
    selectedRecipientAddress,
    setSelectedRecipientAddress,
    recipientName,
    globalAddress,
    hasEnoughBalance,
    isBalanceLoading,
    anyspendQuote,
    isLoadingAnyspendQuote,
    isQuoteLoading,
    activeInputAmountInWei,
    activeOutputAmountInWei,
    geoData,
    coinbaseAvailablePaymentMethods,
    stripeWeb2Support,
    createOrder,
    isCreatingOrder,
    createOnrampOrder,
    isCreatingOnrampOrder
  } = useAnyspendFlow({
    paymentType: effectivePaymentType,
    recipientAddress,
    loadOrder,
    onTransactionSuccess: onSuccess,
    sourceTokenAddress,
    sourceTokenChainId,
    destinationTokenAddress: destinationToken.address,
    destinationTokenChainId: destinationChainId,
    slippage: SLIPPAGE_PERCENT,
    disableUrlParamManagement: true,
    orderType,
    customExactInConfig,
    senderAddress
  });
  const {
    connectedEOAWallet
  } = useAccountWallet();
  const setActiveWallet = useSetActiveWallet();
  const {
    executeDirectTransfer,
    isTransferring: isSwitchingOrExecuting
  } = useDirectTransfer();
  // Check if source and destination are the same token on the same chain
  const isSameChainSameToken = useMemo(() => {
    return effectivePaymentType === "crypto" && selectedSrcChainId === selectedDstChainId && selectedSrcToken?.address?.toLowerCase() === selectedDstToken?.address?.toLowerCase();
  }, [effectivePaymentType, selectedSrcChainId, selectedDstChainId, selectedSrcToken?.address, selectedDstToken?.address]);
  // Check if this is a direct transfer
  const isDirectTransfer = isSameChainSameToken && allowDirectTransfer;
  // State for direct transfer success
  const [directTransferTxHash, setDirectTransferTxHash] = useState();
  // Get gas price for source chain (where the user pays from)
  const {
    gasPrice: gasPriceData,
    isLoading: isLoadingGas
  } = useGasPrice(selectedSrcChainId);
  const appliedPreferEoa = useRef(false);
  useEffect(() => {
    if (preferEoa && !appliedPreferEoa.current) {
      if (connectedEOAWallet) {
        appliedPreferEoa.current = true;
        setActiveWallet(connectedEOAWallet);
      }
    }
  }, [preferEoa, connectedEOAWallet, setActiveWallet]);
  // Prefill destination amount if provided (for EXACT_OUTPUT mode)
  const appliedDestinationAmount = useRef(false);
  useEffect(() => {
    if (destinationTokenAmount && destinationToken?.decimals && !appliedDestinationAmount.current) {
      appliedDestinationAmount.current = true;
      const formattedAmount = formatUnits(destinationTokenAmount, destinationToken.decimals);
      setDstAmountInput(formattedAmount);
      setIsSrcInputDirty(false); // Switch to EXACT_OUTPUT mode
    }
  }, [destinationTokenAmount, destinationToken, setDstAmountInput, setIsSrcInputDirty]);
  const selectedRecipientOrDefault = selectedRecipientAddress ?? recipientAddress;
  const expectedDstAmountRaw = anyspendQuote?.data?.currencyOut?.amount ?? "0";
  const buildCustomPayload = _recipient => {
    if (!customExactInConfig) {
      // For hype_duel or other simple order types
      return {
        expectedDstAmount: expectedDstAmountRaw
      };
    }
    // For custom_exact_in with custom config
    return {
      amount: expectedDstAmountRaw,
      expectedDstAmount: expectedDstAmountRaw,
      functionAbi: customExactInConfig.functionAbi,
      functionName: customExactInConfig.functionName,
      functionArgs: customExactInConfig.functionArgs,
      to: normalizeAddress(customExactInConfig.to),
      spenderAddress: customExactInConfig.spenderAddress ? normalizeAddress(customExactInConfig.spenderAddress) : undefined,
      action: customExactInConfig.action
    };
  };
  const btnInfo = useMemo(() => {
    // Check for empty amount based on trade type
    const isAmountEmpty = tradeType === "EXACT_OUTPUT" ? !dstAmountInput || dstAmountInput === "0" : activeInputAmountInWei === "0";
    const isDirectTransfer_0 = isSameChainSameToken && allowDirectTransfer;
    if (isAmountEmpty) return {
      text: "Enter an amount",
      disable: true,
      error: false,
      loading: false
    };
    if (orderType === "hype_duel" && selectedSrcToken?.address?.toLowerCase() === B3_TOKEN.address.toLowerCase()) {
      return {
        text: "Convert to HYPE using B3",
        disable: false,
        error: false,
        loading: false
      };
    }
    if (isQuoteLoading && !isDirectTransfer_0) return {
      text: "Loading quote...",
      disable: true,
      error: false,
      loading: true
    };
    if (isCreatingOrder || isCreatingOnrampOrder || isSwitchingOrExecuting) return {
      text: isSwitchingOrExecuting ? "Transferring..." : "Creating order...",
      disable: true,
      error: false,
      loading: true
    };
    if (!selectedRecipientOrDefault) return {
      text: "Select recipient",
      disable: false,
      error: false,
      loading: false
    };
    if ((!anyspendQuote || !anyspendQuote.success) && !isDirectTransfer_0) return {
      text: "Get quote error",
      disable: true,
      error: true,
      loading: false
    };
    // Check minimum destination amount if specified (skip for direct transfers)
    if (!isDirectTransfer_0 && minDestinationAmount && anyspendQuote?.data?.currencyOut?.amount && anyspendQuote.data.currencyOut.currency && anyspendQuote.data.currencyOut.currency.decimals != null) {
      const rawAmountInWei = BigInt(anyspendQuote.data.currencyOut.amount);
      const decimals = anyspendQuote.data.currencyOut.currency.decimals;
      const actualAmount = parseFloat(formatUnits(rawAmountInWei.toString(), decimals));
      if (actualAmount < minDestinationAmount) {
        return {
          text: `Minimum ${minDestinationAmount} ${DESTINATION_TOKEN_DETAILS.SYMBOL} deposit`,
          disable: true,
          error: true,
          loading: false
        };
      }
    }
    if (effectivePaymentType === "crypto") {
      if (effectiveCryptoPaymentMethod === CryptoPaymentMethodType.NONE) {
        return {
          text: "Choose payment method",
          disable: false,
          error: false,
          loading: false
        };
      }
      if (!hasEnoughBalance && !isBalanceLoading && effectiveCryptoPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET) {
        return {
          text: "Insufficient balance",
          disable: true,
          error: true,
          loading: false
        };
      }
      // Use different text based on order type and whether it's a direct transfer
      const buttonText = isDirectTransfer_0 ? "Transfer" : orderType === "hype_duel" ? "Continue to deposit" : `Execute ${actionLabel}`;
      return {
        text: buttonText,
        disable: false,
        error: false,
        loading: false
      };
    }
    if (effectivePaymentType === "fiat") {
      if (selectedFiatPaymentMethod === FiatPaymentMethod.NONE) {
        return {
          text: "Select payment method",
          disable: false,
          error: false,
          loading: false
        };
      }
      return {
        text: "Buy",
        disable: false,
        error: false,
        loading: false
      };
    }
    return {
      text: "Continue",
      disable: false,
      error: false,
      loading: false
    };
  }, [activeInputAmountInWei, isQuoteLoading, isCreatingOrder, isCreatingOnrampOrder, isSwitchingOrExecuting, selectedRecipientOrDefault, anyspendQuote, effectivePaymentType, effectiveCryptoPaymentMethod, selectedFiatPaymentMethod, hasEnoughBalance, isBalanceLoading, actionLabel, minDestinationAmount, DESTINATION_TOKEN_DETAILS.SYMBOL, orderType, selectedSrcToken, tradeType, dstAmountInput, isSameChainSameToken, allowDirectTransfer]);
  const onMainButtonClick = async () => {
    if (orderType === "hype_duel" && selectedSrcToken?.address?.toLowerCase() === B3_TOKEN.address.toLowerCase()) {
      onOpenCustomModal?.();
      return;
    }
    if (btnInfo.disable) return;
    if (!selectedRecipientOrDefault) {
      setActivePanel(PanelView.RECIPIENT_SELECTION);
      return;
    }
    if (effectivePaymentType === "crypto") {
      if (effectiveCryptoPaymentMethod === CryptoPaymentMethodType.NONE) {
        setActivePanel(PanelView.CRYPTO_PAYMENT_METHOD);
        return;
      }
      await handleCryptoOrder();
    } else if (effectivePaymentType === "fiat") {
      if (selectedFiatPaymentMethod === FiatPaymentMethod.NONE) {
        setActivePanel(PanelView.FIAT_PAYMENT_METHOD);
        return;
      }
      await handleFiatOrder();
    }
  };
  const headerContent = header ? header({
    anyspendPrice: anyspendQuote,
    isLoadingAnyspendPrice: isLoadingAnyspendQuote
  }) : _jsx("div", {
    className: "mb-4 flex flex-col items-center gap-3 text-center",
    children: _jsxs("div", {
      children: [_jsx("h1", {
        className: "text-as-primary text-xl font-bold",
        children: actionLabel
      }), _jsx("p", {
        className: "text-as-secondary text-sm",
        children: "Pay from any token to execute a custom exact-in transaction."
      })]
    })
  });
  const mainView = _jsxs("div", {
    className: classes?.container || "anyspend-custom-exact-in-container mx-auto flex w-full max-w-[460px] flex-col items-center gap-2",
    children: [headerContent, _jsx("div", {
      className: "relative flex w-full max-w-[calc(100vw-32px)] flex-col gap-2",
      children: _jsxs("div", {
        className: "relative flex w-full max-w-[calc(100vw-32px)] flex-col gap-2",
        children: [effectivePaymentType === "crypto" ? _jsx(CryptoPaySection, {
          selectedSrcChainId: selectedSrcChainId,
          setSelectedSrcChainId: setSelectedSrcChainId,
          selectedSrcToken: selectedSrcToken,
          setSelectedSrcToken: setSelectedSrcToken,
          srcAmount: srcAmount,
          setSrcAmount: setSrcAmount,
          isSrcInputDirty: isSrcInputDirty,
          setIsSrcInputDirty: setIsSrcInputDirty,
          selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod,
          onSelectCryptoPaymentMethod: () => setActivePanel(PanelView.CRYPTO_PAYMENT_METHOD),
          anyspendQuote: anyspendQuote,
          onTokenSelect: onTokenSelect
        }) : _jsx(motion.div, {
          initial: {
            opacity: 0,
            y: 20,
            filter: "blur(10px)"
          },
          animate: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)"
          },
          transition: {
            duration: 0.3,
            delay: 0,
            ease: "easeInOut"
          },
          children: _jsx(PanelOnramp, {
            srcAmountOnRamp: srcAmount,
            setSrcAmountOnRamp: setSrcAmount,
            selectedPaymentMethod: selectedFiatPaymentMethod,
            setActivePanel: setActivePanel,
            _recipientAddress: selectedRecipientOrDefault,
            destinationToken: selectedDstToken,
            destinationChainId: selectedDstChainId,
            dstTokenSymbol: DESTINATION_TOKEN_DETAILS.SYMBOL,
            hideDstToken: true,
            destinationAmount: dstAmount,
            onDestinationTokenChange: () => {},
            onDestinationChainChange: () => {},
            fiatPaymentMethodIndex: PanelView.FIAT_PAYMENT_METHOD,
            recipientSelectionPanelIndex: PanelView.RECIPIENT_SELECTION,
            anyspendQuote: anyspendQuote,
            onShowPointsDetail: () => setActivePanel(PanelView.POINTS_DETAIL),
            onShowFeeDetail: () => setActivePanel(PanelView.FEE_DETAIL),
            customUsdInputValues: customUsdInputValues,
            customRecipientLabel: customRecipientLabel
          })
        }), _jsx("div", {
          className: cn("relative -my-1 flex h-0 items-center justify-center", effectivePaymentType === "fiat" && "hidden"),
          children: _jsx(Button, {
            variant: "ghost",
            className: classes?.swapDirectionButton || "swap-direction-button border-as-stroke bg-as-surface-primary z-10 h-10 w-10 cursor-default rounded-xl border-2 sm:h-8 sm:w-8 sm:rounded-xl",
            children: _jsx("div", {
              className: "relative flex items-center justify-center transition-opacity",
              children: _jsx(ArrowDown, {
                className: "text-as-primary/50 h-5 w-5"
              })
            })
          })
        }), effectivePaymentType === "crypto" && _jsx(CryptoReceiveSection, {
          isDepositMode: false,
          isBuyMode: false,
          effectiveRecipientAddress: selectedRecipientOrDefault,
          recipientName: recipientName || undefined,
          customRecipientLabel: customRecipientLabel,
          onSelectRecipient: () => setActivePanel(PanelView.RECIPIENT_SELECTION),
          dstAmount: isSrcInputDirty && !destinationTokenAmount ? dstAmount : dstAmountInput,
          dstToken: selectedDstToken,
          dstTokenSymbol: DESTINATION_TOKEN_DETAILS.SYMBOL,
          dstTokenLogoURI: DESTINATION_TOKEN_DETAILS.LOGO_URI,
          selectedDstChainId: selectedDstChainId,
          setSelectedDstChainId: () => {},
          setSelectedDstToken: () => {},
          isSrcInputDirty: isSrcInputDirty,
          onChangeDstAmount: value => {
            setIsSrcInputDirty(false);
            setDstAmountInput(value);
          },
          disableAmountInput: !!destinationTokenAmount,
          anyspendQuote: isDirectTransfer ? undefined : anyspendQuote,
          onShowPointsDetail: isDirectTransfer ? undefined : () => setActivePanel(PanelView.POINTS_DETAIL),
          onShowFeeDetail: isDirectTransfer ? undefined : () => setActivePanel(PanelView.FEE_DETAIL)
        })]
      })
    }), _jsx(motion.div, {
      initial: {
        opacity: 0,
        y: 20,
        filter: "blur(10px)"
      },
      animate: {
        opacity: 1,
        y: 0,
        filter: "blur(0px)"
      },
      transition: {
        duration: 0.3,
        delay: 0.2,
        ease: "easeInOut"
      },
      className: cn("mt-4 flex w-full max-w-[460px] flex-col gap-2"),
      children: slots.actionButton ? slots.actionButton({
        onClick: onMainButtonClick,
        disabled: btnInfo.disable,
        loading: btnInfo.loading,
        text: btnInfo.text
      }) : _jsx(ShinyButton, {
        accentColor: "hsl(var(--as-brand))",
        disabled: btnInfo.disable,
        onClick: onMainButtonClick,
        className: btnInfo.error && classes?.mainButtonError || btnInfo.disable && classes?.mainButtonDisabled || classes?.mainButton || cn("as-main-button relative w-full", btnInfo.error ? "!bg-as-red" : btnInfo.disable ? "!bg-as-on-surface-2" : "!bg-as-brand"),
        textClassName: cn(btnInfo.error ? "text-white" : btnInfo.disable ? "text-as-secondary" : "text-white"),
        children: _jsxs("div", {
          className: "flex items-center justify-center gap-2",
          children: [btnInfo.loading && _jsx(Loader2, {
            className: "h-4 w-4 animate-spin"
          }), btnInfo.text]
        })
      })
    }), gasPriceData && !isLoadingGas && effectivePaymentType === "crypto" && !isDirectTransfer && _jsx(GasIndicator, {
      gasPrice: gasPriceData,
      className: classes?.gasIndicator || "mt-2 w-full"
    }), mainFooter ? mainFooter : null]
  });
  const handleCryptoOrder = async () => {
    try {
      const isDirectTransfer_1 = isSameChainSameToken && allowDirectTransfer;
      if (!isDirectTransfer_1) {
        invariant(anyspendQuote, "Relay price is not found");
      }
      invariant(selectedRecipientOrDefault, "Recipient address is not found");
      // Handle direct transfer case (same token/chain) - bypass backend, transfer directly
      if (isDirectTransfer_1) {
        const srcAmountBigInt = BigInt(activeInputAmountInWei);
        const txHash = await executeDirectTransfer({
          chainId: selectedSrcChainId,
          tokenAddress: selectedSrcToken.address,
          recipientAddress: selectedRecipientOrDefault,
          amount: srcAmountBigInt,
          method: effectiveCryptoPaymentMethod
        });
        if (txHash) {
          setDirectTransferTxHash(txHash);
          setActivePanel(PanelView.DIRECT_TRANSFER_SUCCESS);
        }
        return;
      }
      // At this point, anyspendQuote is guaranteed to be defined (invariant above ensures this)
      if (tradeType === "EXACT_OUTPUT") {
        // EXACT_OUTPUT mode: create a custom order (like AnySpendStakeUpside)
        const srcAmountFromQuote = anyspendQuote?.data?.currencyIn?.amount;
        invariant(srcAmountFromQuote, "Source amount from quote is not found");
        const expectedDstAmount = anyspendQuote?.data?.currencyOut?.amount ?? "0";
        const encodedData = generateEncodedData(customExactInConfig, activeOutputAmountInWei);
        createOrder({
          recipientAddress: selectedRecipientOrDefault,
          orderType: "custom",
          srcChain: selectedSrcChainId,
          dstChain: selectedDstChainId,
          srcToken: selectedSrcToken,
          dstToken: selectedDstToken,
          srcAmount: srcAmountFromQuote,
          expectedDstAmount,
          creatorAddress: senderAddress || globalAddress,
          payload: {
            amount: activeOutputAmountInWei,
            data: encodedData,
            to: customExactInConfig ? normalizeAddress(customExactInConfig.to) : undefined,
            spenderAddress: customExactInConfig?.spenderAddress ? normalizeAddress(customExactInConfig.spenderAddress) : undefined
          },
          callbackMetadata
        });
      } else {
        // EXACT_INPUT mode: create custom_exact_in order (original behavior)
        const srcAmountBigInt_0 = BigInt(activeInputAmountInWei);
        const payload = buildCustomPayload(selectedRecipientOrDefault);
        createOrder({
          recipientAddress: selectedRecipientOrDefault,
          orderType,
          srcChain: selectedSrcChainId,
          dstChain: selectedDstChainId,
          srcToken: selectedSrcToken,
          dstToken: selectedDstToken,
          srcAmount: srcAmountBigInt_0.toString(),
          expectedDstAmount: expectedDstAmountRaw,
          creatorAddress: senderAddress || globalAddress,
          payload,
          callbackMetadata
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to create order: " + err.message);
    }
  };
  const handleFiatOrder = async () => {
    try {
      invariant(anyspendQuote, "Relay price is not found");
      invariant(selectedRecipientOrDefault, "Recipient address is not found");
      if (!srcAmount || parseFloat(srcAmount) <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }
      let vendor;
      let paymentMethodString = "";
      if (selectedFiatPaymentMethod === FiatPaymentMethod.COINBASE_PAY) {
        if (coinbaseAvailablePaymentMethods.length === 0) {
          toast.error("Coinbase Pay not available");
          return;
        }
        vendor = "coinbase";
        paymentMethodString = coinbaseAvailablePaymentMethods[0]?.id || "";
      } else if (selectedFiatPaymentMethod === FiatPaymentMethod.STRIPE) {
        if (!stripeWeb2Support || !stripeWeb2Support.isSupport) {
          toast.error("Stripe not available");
          return;
        }
        vendor = "stripe-web2";
      } else {
        toast.error("Please select a payment method");
        return;
      }
      const onrampOptions = {
        vendor,
        paymentMethod: paymentMethodString,
        country: geoData?.country || "US",
        redirectUrl: window.location.origin
      };
      if (tradeType === "EXACT_OUTPUT") {
        // EXACT_OUTPUT mode: create a custom order (like AnySpendStakeUpside)
        const expectedDstAmount_0 = anyspendQuote.data?.currencyOut?.amount ?? "0";
        const encodedData_0 = generateEncodedData(customExactInConfig, activeOutputAmountInWei);
        createOnrampOrder({
          recipientAddress: selectedRecipientOrDefault,
          orderType: "custom",
          dstChain: selectedDstChainId,
          dstToken: selectedDstToken,
          srcFiatAmount: srcAmount,
          onramp: onrampOptions,
          expectedDstAmount: expectedDstAmount_0,
          creatorAddress: senderAddress || globalAddress,
          payload: {
            amount: activeOutputAmountInWei,
            data: encodedData_0,
            to: customExactInConfig ? normalizeAddress(customExactInConfig.to) : undefined,
            spenderAddress: customExactInConfig?.spenderAddress ? normalizeAddress(customExactInConfig.spenderAddress) : undefined
          }
        });
      } else {
        // EXACT_INPUT mode: create custom_exact_in order (original behavior)
        const payload_0 = buildCustomPayload(selectedRecipientOrDefault);
        createOnrampOrder({
          recipientAddress: selectedRecipientOrDefault,
          orderType,
          dstChain: selectedDstChainId,
          dstToken: selectedDstToken,
          srcFiatAmount: srcAmount,
          onramp: onrampOptions,
          expectedDstAmount: expectedDstAmountRaw,
          creatorAddress: senderAddress || globalAddress,
          payload: payload_0
        });
      }
    } catch (err_0) {
      console.error(err_0);
      toast.error("Failed to create order: " + err_0.message);
    }
  };
  const orderDetailsView = _jsx("div", {
    className: "mx-auto w-full max-w-[460px]",
    children: _jsx("div", {
      className: "relative flex flex-col gap-4",
      children: oat && _jsx(OrderDetails, {
        mode: mode,
        order: oat.data.order,
        depositTxs: oat.data.depositTxs,
        relayTxs: oat.data.relayTxs,
        executeTx: oat.data.executeTx,
        refundTxs: oat.data.refundTxs,
        cryptoPaymentMethod: effectivePaymentType === "fiat" ? CryptoPaymentMethodType.NONE : effectiveCryptoPaymentMethod,
        selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod,
        onPaymentMethodChange: setSelectedCryptoPaymentMethod,
        onBack: () => {
          setOrderId(undefined);
          setActivePanel(PanelView.MAIN);
        },
        disableUrlParamManagement: true,
        points: oat.data.points || undefined,
        returnToHomeUrl: returnToHomeUrl,
        returnHomeLabel: returnHomeLabel
      })
    })
  });
  const loadingView = _jsx("div", {
    className: "mx-auto flex w-full flex-col items-center gap-4 p-5",
    children: _jsx("div", {
      className: "text-as-primary",
      children: "Loading order details..."
    })
  });
  const recipientSelectionView = _jsx(RecipientSelection, {
    initialValue: selectedRecipientOrDefault || "",
    onBack: () => setActivePanel(PanelView.MAIN),
    onConfirm: address => {
      setSelectedRecipientAddress(address);
      setActivePanel(PanelView.MAIN);
    }
  });
  const cryptoPaymentMethodView = _jsx(CryptoPaymentMethod, {
    selectedPaymentMethod: selectedCryptoPaymentMethod,
    setSelectedPaymentMethod: setSelectedCryptoPaymentMethod,
    isCreatingOrder: isCreatingOrder,
    onBack: () => setActivePanel(PanelView.MAIN),
    onSelectPaymentMethod: method => {
      setSelectedCryptoPaymentMethod(method);
      setActivePanel(PanelView.MAIN);
    }
  });
  const fiatPaymentMethodView = _jsx(FiatPaymentMethodComponent, {
    selectedPaymentMethod: selectedFiatPaymentMethod,
    setSelectedPaymentMethod: setSelectedFiatPaymentMethod,
    onBack: () => setActivePanel(PanelView.MAIN),
    onSelectPaymentMethod: method_0 => {
      setSelectedFiatPaymentMethod(method_0);
      setActivePanel(PanelView.MAIN);
    },
    srcAmountOnRamp: srcAmount
  });
  const pointsDetailView = _jsx(PointsDetailPanel, {
    pointsAmount: anyspendQuote?.data?.pointsAmount || 0,
    onBack: () => setActivePanel(PanelView.MAIN)
  });
  const feeDetailView = anyspendQuote?.data?.fee ? _jsx(FeeDetailPanel, {
    fee: anyspendQuote.data.fee,
    transactionAmountUsd: effectivePaymentType === "fiat" ? parseFloat(srcAmount) : anyspendQuote.data.currencyIn?.amountUsd ? Number(anyspendQuote.data.currencyIn.amountUsd) : undefined,
    onBack: () => setActivePanel(PanelView.MAIN)
  }) : null;
  const exactInSuccessTitle = content.successTitle || "Transfer Complete!";
  const exactInSuccessDesc = content.successDescription || `${srcAmount} ${selectedSrcToken.symbol} sent on ${getChainName(selectedSrcChainId)}`;
  const exactInReturnLabel = content.returnButtonLabel || returnHomeLabel;
  const directTransferSuccessView = slots.successScreen ? _jsx(_Fragment, {
    children: slots.successScreen({
      title: typeof exactInSuccessTitle === "string" ? exactInSuccessTitle : "Transfer Complete!",
      description: typeof exactInSuccessDesc === "string" ? exactInSuccessDesc : "",
      txHash: directTransferTxHash,
      explorerUrl: directTransferTxHash ? getExplorerTxUrl(selectedSrcChainId, directTransferTxHash || "") : undefined,
      onDone: () => {
        onSuccess?.(srcAmount);
        if (returnToHomeUrl) {
          window.location.href = returnToHomeUrl;
        } else {
          setB3ModalOpen(false);
        }
      },
      returnUrl: returnToHomeUrl,
      returnLabel: exactInReturnLabel || undefined
    })
  }) : _jsxs(motion.div, {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    className: "flex flex-col items-center justify-center gap-6 py-8",
    children: [_jsx(AnimatedCheckmark, {
      className: "h-16 w-16"
    }), _jsxs("div", {
      className: "text-center",
      children: [_jsx("h2", {
        className: "text-as-primary mb-2 text-xl font-bold",
        children: exactInSuccessTitle
      }), _jsx("p", {
        className: "text-as-primary/60 text-sm",
        children: exactInSuccessDesc
      }), _jsxs("p", {
        className: "text-as-primary/60 mt-1 text-sm",
        children: ["to ", selectedRecipientOrDefault?.slice(0, 6), "...", selectedRecipientOrDefault?.slice(-4)]
      })]
    }), directTransferTxHash && _jsx("a", {
      href: getExplorerTxUrl(selectedSrcChainId, directTransferTxHash || ""),
      target: "_blank",
      rel: "noopener noreferrer",
      className: "text-as-brand text-sm underline",
      children: "View Transaction"
    }), _jsx(Button, {
      className: "bg-as-brand hover:bg-as-brand/90 mt-4 w-full rounded-xl py-3 font-semibold text-white",
      onClick: () => {
        onSuccess?.(srcAmount);
        if (returnToHomeUrl) {
          window.location.href = returnToHomeUrl;
        } else {
          setB3ModalOpen(false);
        }
      },
      children: exactInReturnLabel || (returnToHomeUrl ? "Return to Home" : "Done")
    })]
  });
  return _jsx(StyleRoot, {
    children: _jsx("div", {
      className: classes?.root || cn("anyspend-container font-inter bg-as-surface-primary mx-auto w-full max-w-[460px] p-6", mode === "page" && "border-as-border-secondary overflow-hidden rounded-2xl border shadow-xl"),
      children: _jsx(TransitionPanel, {
        activeIndex: orderId ? oat ? PanelView.ORDER_DETAILS : PanelView.LOADING : activePanel === PanelView.ORDER_DETAILS ? PanelView.MAIN : activePanel,
        className: cn("rounded-2xl", {
          "mt-0": mode === "modal"
        }),
        variants: {
          enter: {
            x: 300,
            opacity: 0
          },
          center: {
            x: 0,
            opacity: 1
          },
          exit: {
            x: -300,
            opacity: 0
          }
        },
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30
        },
        children: [_jsx("div", {
          children: mainView
        }, "main-view"), _jsx("div", {
          children: cryptoPaymentMethodView
        }, "crypto-payment-method-view"), _jsx("div", {
          children: fiatPaymentMethodView
        }, "fiat-payment-method-view"), _jsx("div", {
          children: recipientSelectionView
        }, "recipient-selection-view"), _jsx("div", {
          children: orderDetailsView
        }, "order-details-view"), _jsx("div", {
          children: loadingView
        }, "loading-view"), _jsx("div", {
          children: pointsDetailView
        }, "points-detail-view"), _jsx("div", {
          children: feeDetailView
        }, "fee-detail-view"), _jsx("div", {
          children: directTransferSuccessView
        }, "direct-transfer-success-view")]
      })
    })
  });
}